import { Button, Modal, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useGetBagByNumberQuery } from "@/services/madinah/bag";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { AntInput } from "@/CommonElements/AntInput";
import { Col, Row, Spinner } from "reactstrap";
import { Flex, Segmented, Space } from "antd";
import { useState } from "react";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { LabelValue } from "@/Component/Value";
import { Info } from "@/Component/Info";
import { handleFormErrors } from "@/utils/helpers";
import { BagsForm } from "@/Pages/Madinah/Reception/MadinahReceptionList";
import { useAssignBag } from "./ReceptionCenter/hooks/useAssignBag";
import { numbersOnlyRules } from "@/Pages/ProfileForm";


export const AssignBagNumberToManifestModal = ({ show, onClose, arrival }) => {
    const [assignMethod, setAssignMethod] = useState('auto');
    const [bag_number, setBagNumber] = useState(null);
    const { t } = useTranslation();
    const hasBagNumber = !!arrival && !!arrival.bag_no;
    const manifestId = arrival?.manifest_id || arrival?.tdm_id;

    // =================
    // Side Effects
    // =================
    const { assignBag, createAndAssignBag, isPending } = useAssignBag({ manifestId });

    const form = useForm({
        disabled: isPending || hasBagNumber
    });
    const createBagForm = useForm({
        disabled: isPending || hasBagNumber
    });

    const { isError: isBagError, isSuccess: isBagSuccess } = useGetBagByNumberQuery({
        bag_number,
        enabled: assignMethod === "auto" && !!bag_number
    });

    // =================
    // Handlers
    // =================
    const onSubmit = (data) => {
        if (assignMethod === "auto") {
            assignBag(data, {
                onSuccess: () => {
                    onClose();
                },
                onError: (error) => {
                    handleFormErrors(error, form.setError, t);
                }
            });

            return;
        }

        createAndAssignBag(data, {
            onSuccess: () => {
                onClose();
            },
            onError: (error) => {
                handleFormErrors(error, createBagForm.setError, t);
            }
        })

    }



    return (
        <Modal backdrop="static" keyboard={false} show={show} size="lg" centered>
            <Modal.Header>
                <Modal.Title>{assignMethod === "auto" ? t("ASSIGN_BAG_NUMBER_TO_MANIFEST") : t("CREATE_AND_ASSIGN_BAG")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Flex justify="center" gap={2} className="mb-3">
                    <Segmented
                        value={assignMethod}
                        onChange={setAssignMethod}
                        options={[
                            { label: t("ASSIGN_BAG_NUMBER_TO_MANIFEST"), value: 'auto', className: 'flex align-items-center mb-0' },
                            { label: t("CREATE_AND_ASSIGN_BAG"), value: 'manual', className: 'flex align-items-center mb-0' },
                        ]}
                    />
                </Flex>


                {/**
                 * ========================
                 * Auto Assign Bag Number
                 * ========================
                 * This is the form for auto assign bag number
                 * ========================
                 */}
                {assignMethod === "auto" && <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <AutoAssignBagNumber setBagNumber={setBagNumber} bagNumber={bag_number} />
                        <ActionsFooter>
                            <Button

                                disabled={isBagError || !isBagSuccess || !bag_number || isPending} type="submit" variant="primary" className="btn-sm">
                                {isPending && <Spinner size="sm" />}
                                {t("LINK_BAG")}
                            </Button>
                            <Button
                                onClick={onClose}
                                className="btn btn-sm btn-danger"
                                style={{ pointerEvents: isPending ? 'none' : 'auto' }}
                                disabled={isPending}
                                type="button"
                            >{t("CANCEL")}</Button>
                        </ActionsFooter>
                    </form>

                </FormProvider>}


                {/**
                     * ========================
                     * Manual Assign Bag Number
                     * ========================
                     * This is the form for auto assign bag number
                     * ========================
                     */}
                {assignMethod === "manual" && <FormProvider {...createBagForm}>
                    <form onSubmit={createBagForm.handleSubmit(onSubmit)}>
                        <ManualAssignBagNumber />
                        <ActionsFooter>
                            <Button
                                disabled={isPending} type="submit" variant="primary" className="btn-sm">
                                {isPending && <Spinner size="sm" />}
                                {t("CREATE_AND_ASSIGN_BAG")}
                            </Button>
                            <Button
                                onClick={onClose}
                                className="btn btn-sm btn-danger"
                                style={{ pointerEvents: isPending ? 'none' : 'auto' }}
                                disabled={isPending}
                                type="button"
                            >{t("CANCEL")}</Button>
                        </ActionsFooter>
                    </form>
                </FormProvider>}

            </Modal.Body>
        </Modal>
    )
}


const ManualAssignBagNumber = () => {
    return (
        <BagsForm />

    )
}

const AutoAssignBagNumber = ({ setBagNumber, bagNumber }) => {
    const { t } = useTranslation();
    const form = useFormContext();

    // =================
    // Side Effects
    // =================
    const { data: bagRes, isSuccess, isLoading: isLoadingBag, isFetching } = useGetBagByNumberQuery({
        bag_number: bagNumber,
        enabled: false
    });
    const bag = bagRes?.data?.data || {};
    const isLoading = isLoadingBag || isFetching;

    const handleClearBagNumber = () => {
        const bagNumber = form.getValues("bag_no");
        setBagNumber(bagNumber);
    }

    return (
        <>
            <>
                <Controller
                    name="bag_no"
                    control={form.control}
                    rules={{
                        required: t("FIELD_REQUIRED"),
                        ...numbersOnlyRules(t),
                    }}
                    render={({ field, fieldState: { invalid, error } }) => (
                        <Stack gap={2}>
                            <label for={field.name}>{t("BAG_NUMBER")}</label>
                            <Space>
                                <AntInput
                                    {...field}
                                    id={field.name}
                                    status={invalid ? "error" : undefined}
                                    placeholder={t("BAG_NUMBER")}
                                    required
                                />
                                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => handleClearBagNumber()}>{t("SEARCH")}</button>
                            </Space>
                            {error && <div className="text-danger">{error.message}</div>}
                        </Stack>
                    )}

                />
            </>
            {isLoading &&
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
                    <Spinner size="sm" />
                </div>
            }
            {isSuccess && !isLoading && <Row
                style={{
                    border: '1px dashed #000',
                    borderRadius: '10px',
                    paddingInline: '10px',
                    paddingBlock: '5px',
                    marginBlock: '10px',
                    marginInline: '2px',

                }}>
                <Col md={6} style={{ marginBottom: "10px" }}>
                    <LabelValue label={t("BAG_NUMBER")} value={bag.expected_pilgrims_count} />
                </Col>
                <Col md={6} style={{ marginBottom: "10px" }}>
                    <LabelValue label={t("EXPECTED_PILGRIMS_COUNT")} value={bag.actual_passports_count} />
                </Col>

                <Col md={6} style={{ marginBottom: "10px" }}>
                    <LabelValue label={t("GROUPS")} value={bag.classification_type} renderValue={(value) => {
                        return <Space>
                            {value?.length || "-"}
                            {value?.length && <Info title={
                                <ul>
                                    {value?.map((item, index) => <li key={index}>{item}</li>)}
                                </ul>

                            } />}
                        </Space>
                    }} />
                </Col>
                <Col md={6} style={{ marginBottom: "10px" }}>
                    <LabelValue label={t("NOTES")} value={bag.notes} />
                </Col>
            </Row>}
        </>
    )
}